import React, { useEffect, useMemo, useState, type ReactElement } from 'react';
import { graphql, useFragment } from 'react-relay';
import PagesIcon from '@atlaskit/icon/core/pages';
import { SpotlightTarget } from '@atlaskit/onboarding';
import { fg } from '@atlassian/jira-feature-gating';
import {
	BOOLEAN_LITERAL_FALSE,
	IDENTIFIER_ACKNOWLEDGED,
	IDENTIFIER_ASSIGNEE,
	IDENTIFIER_IS_SEEN,
	IDENTIFIER_STATUS,
	LOGICAL_OPERATOR_AND,
	OPERATOR_EQUAL_TO,
	STRING_LITERAL_STATUS_CLOSED,
	STRING_LITERAL_STATUS_OPEN,
} from '@atlassian/eoc-query-editor';
import { useIntl } from '@atlassian/jira-intl';
import { NAVIGATION_ITEM } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/action-subject-type.tsx';
import { FIXED_ITEM } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/component-type.tsx';
import {
	LEVEL_THREE,
	LEVEL_TWO,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/level.tsx';
import { MENU_ID_ANALYTIC_EVENT_IDS } from '@atlassian/jira-navigation-apps-sidebar-nav4-analytics/src/common/constants/analytics/side-nav.tsx';
import {
	ENTITY_ID,
	MENU_ID_OPERATIONS,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/constants.tsx';
import { toMenuIdFromMenuIdList } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/common/utils/menu-id/index.tsx';
import { Nav4ExpandableMenuItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-expandable-menu-item/index.tsx';
import { Nav4MenuLinkItem } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/ui/nav4-menu-link-item/index.tsx';
import { getTestId } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/get-test-id/index.tsx';
import { useFormattedMessageAndIcon } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/utils/use-formatted-message-and-icon/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type {
	AlertsMenuItemContentNew$key,
	AlertsMenuItemContentNew$data,
} from '@atlassian/jira-relay/src/__generated__/AlertsMenuItemContentNew.graphql';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { ExpandableMenuItemContent, ExpandableMenuItemTrigger } from '@atlassian/navigation-system';
import { useIsActiveRoute, useQueryParam } from '@atlassian/react-resource-router';
import { OpsgenieSavedSearchAri } from '@atlassian/ari/opsgenie/saved-search';
import { ALERT_LIST_SPOTLIGHT_TARGETS } from '@atlassian/eoc-alert-common';
import { eocGlobalAlertListRoute } from '@atlassian/jira-router-routes-eoc-global-alerts-routes/src/eocGlobalAlertListRoute.tsx';
import { eocTeamAlertDetailRoute } from '@atlassian/jira-router-routes-eoc-team-alerts-routes/src/eocTeamAlertDetailRoute.tsx';
import commonMessages from '../../../common/messages.tsx';
import messages from '../../messages.tsx';
import { useAlertsPopoutStore } from '../../operations-expandable-menu-item/action-buttons/useAlertsPopoutStore.tsx';
import { AlertsFlyoutMenuItem } from './flyout-menu/index.tsx';
import { SavedSearchActionButtons } from './action-button/index.tsx';

export function AlertsMenuItemContentNew({ queryRef }: { queryRef: AlertsMenuItemContentNew$key }) {
	const data = useFragment<AlertsMenuItemContentNew$key>(
		graphql`
			fragment AlertsMenuItemContentNew on Query {
				opsgenie @required(action: LOG) {
					savedSearches(cloudId: $cloudId) @required(action: LOG) @optIn(to: "opsgenie-beta") {
						sharedWithMe @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
						createdByMe @required(action: THROW) {
							id @required(action: THROW)
							name @required(action: THROW)
							alertSearchQuery @required(action: THROW)
						}
					}
				}
			}
		`,
		queryRef,
	);

	const [savedSearchIdFromURL] = useQueryParam('filter');
	const [alertSearchQueryFromURL] = useQueryParam('query');
	const [selectedFilter, setSelectedFilter] = useState('');
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const accountId = useAccountId();
	const [viewType] = useQueryParam('view');
	const { icon: pagesIcon } = useFormattedMessageAndIcon(commonMessages.alerts, PagesIcon);
	const isAlertsRoute = useIsActiveRoute(
		new Set([eocGlobalAlertListRoute.name, eocTeamAlertDetailRoute.name]),
	);

	const { setLastVisitedSearch } = useAlertsPopoutStore();

	const { sharedWithMe, createdByMe } = data?.opsgenie?.savedSearches || {};

	const defaultFilters = useMemo(
		() => [
			{ id: 'all', name: formatMessage(messages.allSearch), alertSearchQuery: '' },
			{
				id: 'open',
				name: formatMessage(messages.openSearch),
				alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN}`,
			},
			{
				id: 'closed',
				name: formatMessage(messages.closedSearch),
				alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_CLOSED}`,
			},
			{
				id: 'unacked',
				name: formatMessage(messages.unackedSearch),
				alertSearchQuery: `${IDENTIFIER_STATUS}${OPERATOR_EQUAL_TO} ${STRING_LITERAL_STATUS_OPEN} ${LOGICAL_OPERATOR_AND} ${IDENTIFIER_ACKNOWLEDGED}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
			},
			{
				id: 'notSeen',
				name: formatMessage(messages.notSeenSearch),
				alertSearchQuery: `${IDENTIFIER_IS_SEEN}${OPERATOR_EQUAL_TO} ${BOOLEAN_LITERAL_FALSE}`,
			},
			{
				id: 'assignedToMe',
				name: formatMessage(messages.assignedToMeSearch),
				alertSearchQuery: `${IDENTIFIER_ASSIGNEE}${OPERATOR_EQUAL_TO} "${accountId || 'me'}"`,
			},
		],
		[formatMessage, accountId],
	);
	const defaultFilterMenuIds = getParentMenuId(defaultFilters);

	const defaultFilterWithComponentList = getComponentAddedSearchObjects(
		defaultFilters,
		pagesIcon,
		viewType,
		'default',
		savedSearchIdFromURL,
		setSelectedFilter,
		createAnalyticsEvent,
		setLastVisitedSearch,
		isAlertsRoute,
	);
	const sharedWithMeWithComponentList = getComponentAddedSearchObjects(
		sharedWithMe || [],
		pagesIcon,
		viewType,
		'sharedWithMe',
		savedSearchIdFromURL,
		setSelectedFilter,
		createAnalyticsEvent,
		setLastVisitedSearch,
		isAlertsRoute,
	);
	const createdByMeWithComponentList = getComponentAddedSearchObjects(
		createdByMe || [],
		pagesIcon,
		viewType,
		'createdByMe',
		savedSearchIdFromURL,
		setSelectedFilter,
		createAnalyticsEvent,
		setLastVisitedSearch,
		isAlertsRoute,
	);
	const allFiltersWithComponentList = useMemo(
		() => [
			...defaultFilterWithComponentList,
			...sharedWithMeWithComponentList,
			...createdByMeWithComponentList,
		],
		[createdByMeWithComponentList, defaultFilterWithComponentList, sharedWithMeWithComponentList],
	);

	const getSelectedFilterComponentIfNotDefault = useMemo(
		() => (filterId: string) => {
			if (defaultFilters.find((item) => item.id === filterId)) {
				return;
			}

			const foundComponent = [
				...sharedWithMeWithComponentList,
				...createdByMeWithComponentList,
			].find((item) => item.id === filterId)?.component;
			return foundComponent;
		},
		[createdByMeWithComponentList, defaultFilters, sharedWithMeWithComponentList],
	);

	useEffect(() => {
		if (fg('operations_nav4_remaining_items_multi_highlight')) {
			if (selectedFilter === '') {
				const customSearches = [...createdByMeWithComponentList, ...sharedWithMeWithComponentList];
				if (savedSearchIdFromURL) {
					const matchedItem = customSearches.find(
						(item) => OpsgenieSavedSearchAri.parse(item.id).savedSearchId === savedSearchIdFromURL,
					);
					if (matchedItem) {
						setSelectedFilter(matchedItem.id);
						return;
					}
				}
				if (alertSearchQueryFromURL) {
					const matchedItem = customSearches.find(
						(item) => item.alertSearchQuery === alertSearchQueryFromURL,
					);
					if (matchedItem) {
						setSelectedFilter(matchedItem.id);
					}
				}
			}
		}
		if (!fg('operations_nav4_remaining_items_multi_highlight')) {
			if (selectedFilter === '') {
				for (const item of allFiltersWithComponentList) {
					// FIXME: This approach is not ideal. We should somehow use id instead of the alertSearchQuery.
					// https://ifountain.atlassian.net/browse/ALX-9535
					if (item.alertSearchQuery === alertSearchQueryFromURL) {
						setSelectedFilter(item.id);
						break;
					}
				}
			}
		}
	}, [
		alertSearchQueryFromURL,
		allFiltersWithComponentList,
		createdByMeWithComponentList,
		savedSearchIdFromURL,
		selectedFilter,
		sharedWithMeWithComponentList,
	]);

	return (
		<UFOSegment name="nav4.sidebar.operations.alerts">
			<SpotlightTarget name={ALERT_LIST_SPOTLIGHT_TARGETS.sidebar}>
				{/* todo: enable this when starred searches are done.
					<MenuSection>
						<MenuSectionHeading>{formatMessage(messages.starred)}</MenuSectionHeading>
						<MenuLinkItem
							isSelected={selectedFilter === 'allAlerts'}
							elemBefore={pagesIcon}
							href="/jira/ops/alerts"
							onClick={() => setSelectedFilter('allAlerts')}
						>
							{formatMessage(messages.allAlerts)}
						</MenuLinkItem>
						<MenuLinkItem
							isSelected={selectedFilter === 'myAlerts'}
							elemBefore={pagesIcon}
							href="/jira/ops/alerts"
							onClick={() => setSelectedFilter('myAlerts')}
						>
							{formatMessage(messages.myAlerts)}
						</MenuLinkItem>
					</MenuSection> */}
				<>
					<Nav4ExpandableMenuItem
						menuId={defaultFilterMenuIds}
						onExpansionToggle={(hasExpanded) => {
							if (hasExpanded) {
								fireUIAnalytics(createAnalyticsEvent({}), {
									action: 'clicked',
									actionSubject: NAVIGATION_ITEM,
									actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
									attributes: {
										componentType: FIXED_ITEM,
										level: LEVEL_TWO,
										itemId: 'defaultFilters',
										parentItemId: 'alerts',
									},
								});
							}
						}}
					>
						<ExpandableMenuItemTrigger testId={getTestId(defaultFilterMenuIds)}>
							{formatMessage(messages.defaultFilters)}
						</ExpandableMenuItemTrigger>
						<ExpandableMenuItemContent>
							{defaultFilters.map((item) => {
								const href = `/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}`;
								return (
									<Nav4MenuLinkItem
										elemBefore={pagesIcon}
										key={item.id}
										href={href}
										menuId={ENTITY_ID.ALARM_QUEUE(item.alertSearchQuery)}
										onClick={() => {
											setSelectedFilter(item.id);
											if (fg('operations_nav4_remaining_items_local_storage')) {
												setLastVisitedSearch(href);
											}

											fireUIAnalytics(createAnalyticsEvent({}), {
												action: 'clicked',
												actionSubject: NAVIGATION_ITEM,
												actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
												attributes: {
													componentType: FIXED_ITEM,
													level: LEVEL_THREE,
													itemId: item.id,
													parentItemId: 'defaultFilters',
													itemType: 'defaultFilter',
												},
											});
										}}
									>
										{item.name}
									</Nav4MenuLinkItem>
								);
							})}
						</ExpandableMenuItemContent>
					</Nav4ExpandableMenuItem>

					<>{getSelectedFilterComponentIfNotDefault(selectedFilter)}</>

					<AlertsFlyoutMenuItem
						defaultFilterWithComponentList={defaultFilterWithComponentList}
						sharedWithMeWithComponentList={sharedWithMeWithComponentList}
						createdByMeWithComponentList={createdByMeWithComponentList}
						allFiltersWithComponentList={allFiltersWithComponentList}
					/>
				</>
			</SpotlightTarget>
		</UFOSegment>
	);
}

type AlertFilters = NonNullable<
	NonNullable<NonNullable<AlertsMenuItemContentNew$data>['opsgenie']>['savedSearches']
>['createdByMe'];

function getParentMenuId(arr?: AlertFilters) {
	return toMenuIdFromMenuIdList((arr || []).map((i) => ENTITY_ID.ALARM_QUEUE(i.alertSearchQuery)));
}

function getComponentAddedSearchObjects(
	arrayList: AlertFilters,
	pagesIcon: ReactElement,
	viewType: string | undefined,
	savedSearchType: 'default' | 'sharedWithMe' | 'createdByMe',
	savedSearchIdFromURL: string | undefined,
	setSelectedFilter: React.Dispatch<React.SetStateAction<string>>,
	createAnalyticsEvent: ReturnType<typeof useAnalyticsEvents>['createAnalyticsEvent'],
	setLastVisitedSearch: (value: string) => void | Promise<void>,
	isAlertsRoute: boolean,
): Array<AlertFilters[number] & { component: ReactElement }> {
	return arrayList.map((item) => {
		if (fg('operations_nav4_remaining_items_multi_highlight')) {
			const requiresSavedSearchId = item.id && savedSearchType !== 'default';
			const savedSearchId = requiresSavedSearchId
				? OpsgenieSavedSearchAri.parse(item.id).savedSearchId
				: '';
			const href = `/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}${requiresSavedSearchId ? `&filter=${savedSearchId}` : ''}`;

			return {
				...item,
				component: (
					<Nav4MenuLinkItem
						elemBefore={pagesIcon}
						key={item.id}
						href={href}
						menuId={ENTITY_ID.ALARM_QUEUE(
							savedSearchIdFromURL ? savedSearchId : item.alertSearchQuery,
						)}
						onClick={() => {
							setSelectedFilter(item.id);
							if (isAlertsRoute && fg('operations_nav4_remaining_items_local_storage')) {
								setLastVisitedSearch(href);
							}

							fireUIAnalytics(createAnalyticsEvent({}), {
								action: 'clicked',
								actionSubject: NAVIGATION_ITEM,
								actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
								attributes: {
									componentType: FIXED_ITEM,
									level: LEVEL_THREE,
									itemId: item.id,
									parentItemId: 'defaultFilters',
									itemType: 'defaultFilter',
								},
							});
						}}
						{...(savedSearchType === 'createdByMe' && {
							actionsOnHover: <SavedSearchActionButtons savedSearch={item} />,
						})}
					>
						{item.name}
					</Nav4MenuLinkItem>
				),
			};
		}

		return {
			...item,
			component: (() => {
				const href = `/jira/ops/alerts?view=${viewType ?? 'list'}&query=${item.alertSearchQuery}`;

				return (
					<Nav4MenuLinkItem
						elemBefore={pagesIcon}
						key={item.id}
						href={href}
						menuId={ENTITY_ID.ALARM_QUEUE(item.alertSearchQuery)}
						onClick={() => {
							setSelectedFilter(item.id);
							if (isAlertsRoute && fg('operations_nav4_remaining_items_local_storage')) {
								setLastVisitedSearch(href);
							}

							fireUIAnalytics(createAnalyticsEvent({}), {
								action: 'clicked',
								actionSubject: NAVIGATION_ITEM,
								actionSubjectId: MENU_ID_ANALYTIC_EVENT_IDS[MENU_ID_OPERATIONS],
								attributes: {
									componentType: FIXED_ITEM,
									level: LEVEL_THREE,
									itemId: item.id,
									parentItemId: 'defaultFilters',
									itemType: 'defaultFilter',
								},
							});
						}}
						{...(savedSearchType === 'createdByMe' && {
							actionsOnHover: <SavedSearchActionButtons savedSearch={item} />,
						})}
					>
						{item.name}
					</Nav4MenuLinkItem>
				);
			})(),
		};
	});
}
